import { useState, useEffect } from "react";
import api from "../lib/api";
import { X, Search, Check } from "lucide-react";
import { resolveImageUrl } from "../lib/url";

/**
 * Modal for picking an image from the gallery.
 * Props:
 *   open, onClose, onPick(url) — url is what gets stored (can be /api/files/... or external)
 */
const GalleryPicker = ({ open, onClose, onPick }) => {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    if (!open) return;
    api.get("/admin/gallery").then(r => setItems(r.data));
  }, [open]);

  if (!open) return null;

  const cats = ["all", "general", "music", "dance", "event", "workshop"];
  const filtered = items.filter(it => {
    if (filter !== "all" && it.category !== filter) return false;
    if (search && !((it.title || "") + " " + (it.url || "")).toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="gallery-picker">
      <div className="bg-card border border-border rounded-xl max-w-4xl w-full max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="font-serif-display text-xl">Bild aus Galerie wählen</div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 border-b border-border flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Suchen…"
              className="w-full bg-input/30 border border-border rounded-md pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-primary"
            />
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {cats.map(c => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-[0.15em] ${filter === c ? "bg-primary text-primary-foreground" : "border border-border text-muted-foreground hover:text-foreground"}`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
        <div className="overflow-y-auto p-4 flex-1">
          {filtered.length === 0 && <div className="text-center text-muted-foreground py-12">Keine Bilder gefunden</div>}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {filtered.map(it => (
              <button
                key={it.id}
                type="button"
                onClick={() => { onPick(it.url, it); onClose(); }}
                data-testid={`pick-${it.id}`}
                className="card-academy rounded-md overflow-hidden text-left group"
              >
                <div className="aspect-square overflow-hidden bg-secondary">
                  <img src={resolveImageUrl(it.url)} alt={it.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                </div>
                <div className="p-2">
                  <div className="text-xs truncate">{it.title || "—"}</div>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-primary">{it.category}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GalleryPicker;
